import { useState, useCallback, useRef, useEffect } from 'react';
import { useSupplierAuth } from '../context/SupplierAuthContext';
import { supplierNotificationAPI } from '../services/supplierApi';

/**
 * useSupplierNotifications – notifications hook for the Supplier portal.
 *
 * Mirrors the business-side useBusinessNotifications hook but uses
 * supplier-portal API endpoints with the supplier JWT.
 */
export default function useSupplierNotifications() {
  const { isAuthenticated } = useSupplierAuth();

  const [items, setItems]           = useState([]);
  const [meta, setMeta]             = useState({});
  const [loading, setLoading]       = useState(false);
  const [unreadCount, setUnread]    = useState(0);
  const [filter, setFilter]         = useState('all');
  const [category, setCategory]     = useState('');
  const pageRef   = useRef(1);
  const hasMore   = useRef(true);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const fetchPage = useCallback(async (page, append = false) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const params = { page, limit: 20, filter };
      if (category) params.category = category;

      const result = await supplierNotificationAPI.list(params);
      if (!mountedRef.current) return;

      const newItems  = result.items || result.data || [];
      const pageMeta  = result.meta  || {};

      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setMeta(pageMeta);
      hasMore.current = page < (pageMeta.totalPages || 1);
    } catch (err) {
      console.error('[useSupplierNotifications] fetch error', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isAuthenticated, filter, category]);

  useEffect(() => {
    if (!isAuthenticated) { setItems([]); setUnread(0); return; }
    pageRef.current = 1;
    hasMore.current = true;
    fetchPage(1);
  }, [fetchPage, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    supplierNotificationAPI.unreadCount()
      .then(r => mountedRef.current && setUnread(r?.count ?? r ?? 0))
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      supplierNotificationAPI.unreadCount()
        .then(r => mountedRef.current && setUnread(r?.count ?? r ?? 0)).catch(() => {});
      if (pageRef.current === 1) fetchPage(1);
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(poll, 30_000);
    return () => { document.removeEventListener('visibilitychange', onVisibility); clearInterval(timer); };
  }, [fetchPage, isAuthenticated]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore.current) return;
    pageRef.current += 1;
    fetchPage(pageRef.current, true);
  }, [loading, fetchPage]);

  const refresh = useCallback(() => {
    pageRef.current = 1;
    hasMore.current = true;
    fetchPage(1);
    supplierNotificationAPI.unreadCount()
      .then(r => mountedRef.current && setUnread(r?.count ?? r ?? 0))
      .catch(() => {});
  }, [fetchPage]);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await supplierNotificationAPI.markRead(id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true, isRead: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  }, []);

  return {
    items,
    meta,
    loading,
    hasMore: hasMore.current,
    unreadCount,
    filter,
    setFilter,
    category,
    setCategory,
    loadMore,
    refresh,
    handleMarkRead,
    setUnreadCount: setUnread,
  };
}
